/**
 * Safe Render Utility
 * 
 * Provides utilities to safely render values that might be objects with {value, unit} structure
 * to prevent React error #31 (objects cannot be rendered as React children)
 */

import React from 'react';

/**
 * Safely extracts a renderable value from potential objects
 * @param {any} value - The value to make safe for rendering
 * @returns {string|number|null} - A safe value for React rendering
 */
export const safeRenderValue = (value) => {
  // Handle null, undefined, or empty values
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  // If it's already a primitive value, return it
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  // Handle objects with {value, unit} structure
  if (typeof value === 'object' && value !== null) {
    // If it has a value property, extract it
    if (value.hasOwnProperty('value') && value.value !== undefined) {
      return safeRenderValue(value.value); // Recursive call to handle nested objects
    }
    
    // If it's an array, join the elements
    if (Array.isArray(value)) {
      return value.map(item => safeRenderValue(item)).join(', ');
    }
    
    // For other objects, try to get a meaningful string representation
    if (value.toString && typeof value.toString === 'function' && value.toString() !== '[object Object]') {
      return value.toString();
    }
    
    // Last resort: JSON stringify
    try {
      return JSON.stringify(value);
    } catch (error) {
      return '[Object]';
    }
  }

  // Fallback for any other type
  return String(value);
};

/**
 * Safely extracts a numeric value from potential objects
 * @param {any} value - The value to extract numeric value from
 * @returns {number|null} - A numeric value or null if not available
 */
export const safeExtractNumericValue = (value) => {
  // Handle null, undefined, or empty values
  if (value === null || value === undefined || value === '') {
    return null;
  }

  // If it's already a number, return it
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }

  // If it's a string that can be converted to number
  if (typeof value === 'string') {
    const numValue = parseFloat(value);
    return isNaN(numValue) ? null : numValue;
  }

  // Handle objects with {value, unit} structure
  if (typeof value === 'object' && value !== null && value.hasOwnProperty('value')) {
    return safeExtractNumericValue(value.value); // Recursive call
  }

  return null;
};

/**
 * Safely formats a value with units for display
 * @param {any} value - The value to format
 * @param {string} unit - The unit to append (optional)
 * @returns {string} - Formatted value with unit
 */
export const safeFormatValueWithUnit = (value, unit = '') => {
  const safeValue = safeRenderValue(value);
  
  if (safeValue === 'N/A' || safeValue === null || safeValue === undefined) {
    return 'N/A';
  }

  if (unit && unit.trim() !== '') {
    return `${safeValue} ${unit.trim()}`;
  }

  return String(safeValue);
};

/**
 * React component wrapper that safely renders any value
 * @param {Object} props - Component props
 * @param {any} props.value - The value to render safely
 * @param {string} props.unit - Optional unit to append
 * @param {Object} props.style - Optional inline styles
 * @param {string} props.fallback - Fallback text if value is not renderable
 * @returns {JSX.Element} - Safe React element
 */
export const SafeRender = ({ value, unit = '', style = {}, fallback = 'N/A' }) => {
  try {
    const safeValue = safeRenderValue(value);
    const displayValue = unit ? safeFormatValueWithUnit(safeValue, unit) : safeValue;
    
    return <span style={style}>{displayValue || fallback}</span>;
  } catch (error) {
    console.error('SafeRender error:', error, 'Value:', value);
    return <span style={{ ...style, color: 'red' }}>[Render Error]</span>;
  }
};

/**
 * Higher-order component that wraps any component with safe rendering
 * @param {React.Component} WrappedComponent - Component to wrap
 * @returns {React.Component} - Wrapped component with safe rendering
 */
export const withSafeRender = (WrappedComponent) => {
  return function SafeRenderWrapper(props) {
    try {
      return <WrappedComponent {...props} />;
    } catch (error) {
      if (error.message && error.message.includes('Objects are not valid as a React child')) {
        console.error('SafeRender caught object rendering error:', error, 'Props:', props);
        return <span style={{ color: 'red' }}>[Component Render Error]</span>;
      }
      throw error; // Re-throw if it's not the object rendering error
    }
  };
};

export default {
  safeRenderValue,
  safeExtractNumericValue,
  safeFormatValueWithUnit,
  SafeRender,
  withSafeRender
};
